import { isLeft, isRight } from 'fp-ts/Either';
import { useCallback, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { Button } from '../../components/ui/button';
import { useAsyncData } from '../../hooks/useAsyncData';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useRequireAuth } from '../../hooks/useRequireAuth';
import { useTRPC } from '../../hooks/useTRPC';
import { captureAndReportError } from '../../utils/errorReporting';
import {
  ExperimentStatusBadge,
  RunStatusBadge,
} from './components/StatusBadges';
import { formatDate } from './utils';

type StoredArtifact = {
  artifactType: 'input_snapshot' | 'manifest' | 'shard';
  artifactKey: string;
  body: unknown;
  created: string;
};

type ShardSummary = {
  artifactKey: string;
  userCount: number | null;
  created: string;
};

type RunDetail = {
  run: {
    id: string;
    status: string;
    audienceMode: 'limited' | 'all';
    requestedUserLimit: number | null;
    whitelistAppCount: number;
    eligibleUserCount: number | null;
    outputUserCount: number | null;
    inputSnapshotPointer: string | null;
    outputArtifactPointer: string | null;
    qaReport: unknown;
    errorMessage: string | null;
    workerId: string | null;
    lastHeartbeatAt: string | null;
    progressUserCount: number | null;
    progressMessage: string | null;
    requestedBy: { id: string; email: string; fullName: string } | null;
    startedAt: string;
    completedAt: string | null;
  };
  experiment: {
    id: string;
    experimentId: string;
    weekId: string;
    status: string;
    whitelistAndroidPackages: string[];
    notes: string | null;
  };
  manifest: StoredArtifact | null;
  inputSnapshot: StoredArtifact | null;
  shards: ShardSummary[];
  selectedShard: StoredArtifact | null;
};

function JsonBlock({ value }: { value: unknown }) {
  const text = (() => {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  })();

  return (
    <pre className="max-h-96 overflow-auto rounded-xl border border-neutral-800 bg-neutral-950/60 p-3 font-mono text-xs text-neutral-200">
      {text}
    </pre>
  );
}

function QaReportSection({ qaReport }: { qaReport: unknown }) {
  return (
    <section className="rounded-2xl border border-neutral-700 bg-neutral-800/50">
      <div className="border-b border-neutral-700 px-6 py-4">
        <h3 className="text-base font-semibold text-white">QA Report</h3>
      </div>
      <div className="p-6">
        {qaReport ? (
          <JsonBlock value={qaReport} />
        ) : (
          <p className="text-sm text-neutral-500">No QA report yet.</p>
        )}
      </div>
    </section>
  );
}

function MetadataItem({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
        {label}
      </div>
      <div className="mt-1 text-sm text-neutral-200 break-all">{children}</div>
    </div>
  );
}

export function PersonalizationRunDetailPage() {
  const { canRender, authState } = useRequireAuth({
    requireRole: 'admin',
  });
  const trpc = useTRPC();
  const { runId } = useParams<{ runId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data: detail,
    isLoading,
    error,
    run,
  } = useAsyncData<RunDetail | null>({
    initialData: null,
    initialLoading: true,
  });

  const selectedShardKey = searchParams.get('shard');

  usePageTitle('App Spotlight Run | Admin');

  const loadDetail = useCallback(async () => {
    if (!runId) {
      return;
    }

    await run(
      () =>
        trpc.getAppSpotlightPersonalizationRunDetail.query({
          runId,
          shardArtifactKey: selectedShardKey ?? undefined,
        }),
      {
        onSuccess: (result, helpers) => {
          if (isRight(result)) {
            helpers.setData(result.right as RunDetail);
          } else if (isLeft(result)) {
            helpers.setError(result.left.message);
            helpers.setData(null);
          }
        },
        onError: (err, helpers) => {
          captureAndReportError(err, {
            operation: 'admin.getAppSpotlightPersonalizationRunDetail',
            component: 'PersonalizationRunDetailPage',
            runId,
          });
          helpers.setError('Failed to load personalization run');
          toast.error('Failed to load personalization run');
        },
      },
    );
  }, [run, runId, selectedShardKey, trpc]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  const onSelectShard = (artifactKey: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (artifactKey) {
      next.set('shard', artifactKey);
    } else {
      next.delete('shard');
    }
    setSearchParams(next, { replace: true });
  };

  if (!canRender || authState.type !== 'Authenticated') {
    return null;
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Link
              to="/app-spotlight"
              className="text-sm text-neutral-400 hover:text-white"
            >
              ← App Spotlight
            </Link>
            <h1 className="mt-2 text-2xl font-semibold text-white">
              Personalization Run
            </h1>
            <p className="text-sm text-neutral-400">
              Private DB-backed artifacts. Outputs hold hashed user keys only.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void loadDetail()}
            disabled={isLoading}
          >
            Refresh
          </Button>
        </div>

        {error && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            {error}
          </div>
        )}

        {isLoading && !detail ? (
          <div className="rounded-2xl border border-neutral-700 bg-neutral-800/50 p-6 text-sm text-neutral-400">
            Loading run details…
          </div>
        ) : detail ? (
          <RunDetailContent
            detail={detail}
            selectedShardKey={selectedShardKey}
            onSelectShard={onSelectShard}
          />
        ) : null}
      </div>
    </div>
  );
}

function RunDetailContent({
  detail,
  selectedShardKey,
  onSelectShard,
}: {
  detail: RunDetail;
  selectedShardKey: string | null;
  onSelectShard: (artifactKey: string | null) => void;
}) {
  const { run, experiment, manifest, inputSnapshot, shards, selectedShard } =
    detail;

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-neutral-700 bg-neutral-800/50">
        <div className="border-b border-neutral-700 px-6 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-white">
                Experiment {experiment.experimentId}
              </h2>
              <p className="mt-1 text-sm text-neutral-400">
                Week {experiment.weekId}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <ExperimentStatusBadge status={experiment.status} />
              <RunStatusBadge status={run.status} />
            </div>
          </div>
        </div>
        <div className="grid gap-4 p-6 sm:grid-cols-2 lg:grid-cols-3">
          <MetadataItem label="Run id">
            <span className="font-mono">{run.id}</span>
          </MetadataItem>
          <MetadataItem label="Worker">
            {run.workerId ?? (
              <span className="text-neutral-500">not claimed</span>
            )}
          </MetadataItem>
          <MetadataItem label="Audience">
            {run.audienceMode}
            {run.requestedUserLimit ? ` (limit ${run.requestedUserLimit})` : ''}
          </MetadataItem>
          <MetadataItem label="Eligible users">
            {run.eligibleUserCount ?? '—'}
          </MetadataItem>
          <MetadataItem label="Output users">
            {run.outputUserCount ?? '—'}
          </MetadataItem>
          <MetadataItem label="Whitelist apps">
            {run.whitelistAppCount}
          </MetadataItem>
          <MetadataItem label="Started">
            {formatDate(run.startedAt)}
          </MetadataItem>
          <MetadataItem label="Heartbeat">
            {formatDate(run.lastHeartbeatAt)}
          </MetadataItem>
          <MetadataItem label="Completed">
            {formatDate(run.completedAt)}
          </MetadataItem>
          <MetadataItem label="Requested by">
            {run.requestedBy?.email ?? (
              <span className="text-neutral-500">unknown</span>
            )}
          </MetadataItem>
          <MetadataItem label="Input snapshot key">
            {run.inputSnapshotPointer ?? (
              <span className="text-neutral-500">pending</span>
            )}
          </MetadataItem>
          <MetadataItem label="Manifest key">
            {run.outputArtifactPointer ?? (
              <span className="text-neutral-500">pending</span>
            )}
          </MetadataItem>
        </div>

        {run.progressMessage && (
          <div className="border-t border-neutral-700 px-6 py-3 text-sm text-neutral-300">
            {run.progressMessage}
            {run.progressUserCount != null
              ? ` · ${run.progressUserCount} users`
              : ''}
          </div>
        )}

        {run.errorMessage && (
          <div className="border-t border-red-500/30 bg-red-500/10 px-6 py-3 text-sm text-red-200">
            {run.errorMessage}
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-neutral-700 bg-neutral-800/50">
        <div className="border-b border-neutral-700 px-6 py-4">
          <h3 className="text-base font-semibold text-white">Whitelist</h3>
          <p className="mt-1 text-xs text-neutral-400">
            {experiment.whitelistAndroidPackages.length} apps approved for this
            experiment
          </p>
        </div>
        <div className="flex flex-wrap gap-2 p-6">
          {experiment.whitelistAndroidPackages.length === 0 ? (
            <span className="text-sm text-neutral-500">No packages</span>
          ) : (
            experiment.whitelistAndroidPackages.map((pkg) => (
              <span
                key={pkg}
                className="rounded-full border border-neutral-700 bg-neutral-900 px-3 py-1 font-mono text-xs text-neutral-200"
              >
                {pkg}
              </span>
            ))
          )}
        </div>
      </section>

      <QaReportSection qaReport={run.qaReport} />

      <section className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-neutral-700 bg-neutral-800/50">
          <div className="border-b border-neutral-700 px-6 py-4">
            <h3 className="text-base font-semibold text-white">Manifest</h3>
            <p className="mt-1 text-xs text-neutral-400">
              {manifest ? manifest.artifactKey : 'Not yet persisted'}
            </p>
          </div>
          <div className="p-6">
            {manifest ? (
              <JsonBlock value={manifest.body} />
            ) : (
              <p className="text-sm text-neutral-500">
                Manifest will appear once the worker completes the run.
              </p>
            )}
          </div>
        </div>
        <div className="rounded-2xl border border-neutral-700 bg-neutral-800/50">
          <div className="border-b border-neutral-700 px-6 py-4">
            <h3 className="text-base font-semibold text-white">
              Input snapshot
            </h3>
            <p className="mt-1 text-xs text-neutral-400">
              {inputSnapshot ? inputSnapshot.artifactKey : 'Not yet persisted'}
            </p>
          </div>
          <div className="p-6">
            {inputSnapshot ? (
              <JsonBlock value={inputSnapshot.body} />
            ) : (
              <p className="text-sm text-neutral-500">
                Input snapshot will appear once the worker completes the run.
              </p>
            )}
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-neutral-700 bg-neutral-800/50">
        <div className="border-b border-neutral-700 px-6 py-4">
          <h3 className="text-base font-semibold text-white">Shards</h3>
          <p className="mt-1 text-xs text-neutral-400">
            {shards.length} shard{shards.length === 1 ? '' : 's'} stored in DB
          </p>
        </div>
        {shards.length === 0 ? (
          <div className="p-6 text-sm text-neutral-500">No shards yet.</div>
        ) : (
          <div className="grid gap-0 divide-y divide-neutral-800">
            {shards.map((shard) => {
              const isSelected =
                (selectedShardKey ?? selectedShard?.artifactKey) ===
                shard.artifactKey;
              return (
                <button
                  type="button"
                  key={shard.artifactKey}
                  onClick={() =>
                    onSelectShard(isSelected ? null : shard.artifactKey)
                  }
                  className={`flex w-full items-center justify-between px-6 py-3 text-left text-sm transition-colors hover:bg-neutral-900/60 ${
                    isSelected ? 'bg-neutral-900/80' : ''
                  }`}
                >
                  <span className="font-mono text-xs text-neutral-200 break-all">
                    {shard.artifactKey}
                  </span>
                  <span className="ml-4 text-xs text-neutral-400">
                    {shard.userCount ?? '?'} users · {formatDate(shard.created)}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {selectedShard && (
        <section className="rounded-2xl border border-neutral-700 bg-neutral-800/50">
          <div className="border-b border-neutral-700 px-6 py-4">
            <h3 className="text-base font-semibold text-white">Shard output</h3>
            <p className="mt-1 text-xs text-neutral-400 break-all">
              {selectedShard.artifactKey} · hashed user keys with package
              recommendations only
            </p>
          </div>
          <div className="p-6">
            <JsonBlock value={selectedShard.body} />
          </div>
        </section>
      )}
    </div>
  );
}
